import React, {useEffect, useState} from 'react';
import {makeRequest} from "../../utils/httpRequest";
import ProductSelectAsync from "../product/ProductSelectAsync";
import CustomerSelectAsync from "../customer/CustomerSelectAsync";
import DatePicker from "react-datepicker";
import 'react-datepicker/dist/react-datepicker.css';
import {getProductById} from "../../utils/requestUtils";
import {formatLocalDate, parseLocalDate, UserPermission, CompanyPermission} from "../../utils/utils";
import PaymentMethodValue from "../paymentMehod/PaymentMethodValueSelect";
import Config from "../../core/config";
import {useRecoilState} from "recoil";
import {loadingAtom} from "../../atoms";
import ActionButtonsManagerView from "../common/ActionButtonsManagerView";
import ConfirmButton from "../common/ConfirmButton";
import DeleteButton from "../common/DeleteButton";
import DecimalInput from "../common/DecimalInput";
import {useNavigate} from "react-router-dom";
import Loading from "../../core/Loading";
import {usePermission} from "../../core/PermissionContext";
import { showConfirm, showAlert } from '../../core/dialog';

const apiUrl = Config.apiUrl;

const emptySaleItem = {
    product: {id: undefined, name: ''},
    quantity: 1,
    value: 0,
    total: 0,
    availableStock: null,
};

const ProductSaleManager = () => {
    const {hasUserPermission, hasCompanyPermission, loadingPermissions} = usePermission();
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(window.location.search);
    const id = searchParams.get('id');

    const newEntity = {
        id: undefined,
        customer: undefined,
        saleDate: formatLocalDate(new Date()),
        saleItems: [],
        paymentMethods: [],
        receivables: [],
        totalValue: 0,
        details: '',
    };

    const [formData, setFormData] = useState(newEntity);
    const [saleItems, setSaleItems] = useState([{...emptySaleItem}]);
    const [showPaymentMethodValueSelect, setShowPaymentMethodValueSelect] = useState(false);
    const [loading, setLoading] = useRecoilState(loadingAtom);

    useEffect(() => {
        if (id) {
            makeRequest(`${apiUrl}/api/product-sales/id=${id}`, 'GET')
                .then(async response => {
                    setFormData(response);
                    const itemsWithStock = await Promise.all(response.saleItems.map(async item => {
                        try {
                            const product = await getProductById(item.product.id);
                            // Add back the original quantity because the sale already decreased the stock
                            return {...item, availableStock: product.stock + item.quantity};
                        } catch {
                            return {...item, availableStock: null};
                        }
                    }));
                    setSaleItems([...itemsWithStock, {...emptySaleItem}]);
                })
                .catch(err => console.error(err));
        }
    }, [id]);

    useEffect(() => {
        let total = 0;
        saleItems.forEach(item => total += (item.total || 0));
        setFormData(prev => ({...prev, totalValue: total}));
    }, [saleItems]);

    async function fetchCustomer(customerId) {
        return await makeRequest(`${apiUrl}/api/customers/id=${customerId}`, 'GET');
    }

    const handleCustomerItemClick = async (customerId) => {
        const customer = await fetchCustomer(customerId);
        setFormData(prev => ({...prev, customer}));
    };

    const handleDateChange = (date) => {
        if (date) {
            setFormData(prev => ({...prev, saleDate: formatLocalDate(date)}));
        }
    };

    const handleChange = (e) => {
        const {name, value} = e.target;
        setFormData(prev => ({...prev, [name]: value}));
    };

    const appendEmptyRow = (items, index) => {
        if (index !== items.length - 1) return items;
        return [...items, {...emptySaleItem}];
    };

    const handleProductItemClick = async (productId, index) => {
        setLoading(true);
        try {
            const product = await getProductById(productId);
            const unitPrice = product.value || 0;
            const quantity = saleItems[index].quantity || 1;
            setSaleItems(prev => {
                const updated = prev.map((item, i) => i === index ? {
                    ...item,
                    product: {id: product.id, name: product.name, code: product.code},
                    value: unitPrice,
                    total: unitPrice * quantity,
                    availableStock: product.stock,
                } : item);
                return appendEmptyRow(updated, index);
            });
        } finally {
            setLoading(false);
        }
    };

    const handleQuantityChange = (index, quantity) => {
        const item = saleItems[index];
        if (item.availableStock !== null && quantity > item.availableStock) {
            showAlert(`Estoque insuficiente para "${item.product.name}". Disponível: ${item.availableStock}.`);
        }
        setSaleItems(prev => prev.map((item, i) => i === index ? {
            ...item,
            quantity,
            total: quantity * (item.value || 0),
        } : item));
    };

    const handleValueChange = (index, value) => {
        const numValue = value || 0;
        setSaleItems(prev => prev.map((item, i) => i === index ? {
            ...item,
            value: numValue,
            total: (item.quantity || 1) * numValue,
        } : item));
    };

    const removeItem = (index) => {
        setSaleItems(prev => {
            const next = [...prev];
            next.splice(index, 1);
            return next;
        });
    };

    const validate = () => {
        if (!formData.customer || !formData.customer.id) {
            showAlert('Informe o cliente.');
            return false;
        }
        if (saleItems.length === 1) {
            showAlert('Informe ao menos 1 item.');
            return false;
        }
        for (const item of saleItems.slice(0, -1)) {
            if (item.availableStock !== null && item.quantity > item.availableStock) {
                showAlert(`Estoque insuficiente para "${item.product.name}". Disponível: ${item.availableStock}, solicitado: ${item.quantity}.`);
                return false;
            }
        }
        return true;
    };

    const onConfirm = async () => {
        if (!validate()) return;
        if (formData.totalValue > 0) {
            setShowPaymentMethodValueSelect(true);
        } else {
            if (await showConfirm('O valor da venda está zerado. Deseja realmente confirmar?')) {
                saveEntity([]);
            }
        }
    };

    const saveEntity = async (paymentMethodValues) => {
        const body = {
            id: formData.id,
            code: formData.code,
            customer: formData.customer,
            saleItems: saleItems.slice(0, -1),
            saleDate: formData.saleDate,
            paymentMethods: paymentMethodValues,
            totalValue: formData.totalValue,
            details: formData.details,
        };
        try {
            setLoading(true);
            await makeRequest(
                `${apiUrl}/api/product-sales`,
                id ? 'PUT' : 'POST',
                {'Content-Type': 'application/json'},
                JSON.stringify(body)
            );
            navigate('/app/productsalelist');
        } catch (error) {
            console.error('Error saving:', error);
            showAlert('Erro ao salvar.');
        } finally {
            setLoading(false);
        }
    };

    if (loadingPermissions) return <Loading />;
    if (id && !(hasUserPermission(UserPermission.EDIT_PRODUCT_SALE) && hasCompanyPermission(CompanyPermission.EDIT_PRODUCT_SALE))) {
        navigate('/unauthorizedaccess');
        return null;
    }
    if (!id && !(hasUserPermission(UserPermission.CREATE_PRODUCT_SALE) && hasCompanyPermission(CompanyPermission.CREATE_PRODUCT_SALE))) {
        navigate('/unauthorizedaccess');
        return null;
    }

    return (
        <div className="app-form-page">
            {showPaymentMethodValueSelect && (
                <PaymentMethodValue
                    onClose={() => setShowPaymentMethodValueSelect(false)}
                    paymentMethods={formData.paymentMethods}
                    considerPaidAccordingToDueDate={false}
                    totalValue={formData.totalValue}
                    onConfirm={(paymentMethodValues) => {
                        setShowPaymentMethodValueSelect(false);
                        saveEntity(paymentMethodValues);
                    }}
                />
            )}

            <div className="header-label">
                {(formData.id > 0 ? 'Editar' : 'Nova') + ' Venda' + (formData.id > 0 ? ' - ' + formData.code : '')}
            </div>

            <div style={{display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '5px', alignItems: 'flex-end'}}>
                <div>
                    <div style={{width: '180px'}}>Cliente:</div>
                    <CustomerSelectAsync
                        onItemClick={handleCustomerItemClick}
                        prefilledCustomer={formData.customer}
                        dataTest="customer-async-select"
                    />
                </div>
                <div>
                    <div>Data:</div>
                    <DatePicker
                        selected={parseLocalDate(formData.saleDate)}
                        onChange={handleDateChange}
                        dateFormat="dd/MM/yyyy"
                        autoComplete="off"
                        className="datepicker"
                    />
                </div>
            </div>

            <div style={{marginTop: '5px'}}>
                <table style={{width: '700px'}}>
                    <colgroup>
                        <col style={{width: '300px'}} />
                        <col style={{width: '80px'}} />
                        <col style={{width: '120px'}} />
                        <col style={{width: '120px'}} />
                        <col style={{width: '60px'}} />
                    </colgroup>
                    <thead>
                        <tr>
                            <th>Produto</th>
                            <th>Quantidade</th>
                            <th>Valor Unit. (R$)</th>
                            <th>Total (R$)</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {saleItems.map((item, index) => (
                            <tr key={index}>
                                <td>
                                    <ProductSelectAsync
                                        style={{width: '280px'}}
                                        value={item.product && item.product.id ? item.product.id : null}
                                        onItemClick={(productId) => handleProductItemClick(productId, index)}
                                        prefilledProduct={item.product && item.product.id ? item.product : null}
                                        index={index}
                                    />
                                </td>
                                {index < saleItems.length - 1 && (
                                    <>
                                        <td>
                                            <input
                                                type="number"
                                                style={{width: '70px', borderColor: item.availableStock !== null && item.quantity > item.availableStock ? 'red' : undefined}}
                                                step="1"
                                                value={item.quantity}
                                                onChange={(e) => handleQuantityChange(index, parseFloat(e.target.value))}
                                            />
                                            {item.availableStock !== null && (
                                                <div style={{fontSize: '11px', color: item.quantity > item.availableStock ? 'red' : '#888'}}>
                                                    estoque: {item.availableStock}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <DecimalInput
                                                style={{width: '110px', textAlign: 'right'}}
                                                placeholder="0,00"
                                                value={item.value}
                                                onChange={(e) => handleValueChange(index, e.target.value)}
                                            />
                                        </td>
                                        <td>
                                            <div style={{width: '110px', textAlign: 'right'}}>
                                                {(item.total || 0).toLocaleString('pt-BR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                            </div>
                                        </td>
                                        <td>
                                            <DeleteButton onClick={async () => {
                                                if (await showConfirm('Deseja realmente excluir este item?')) {
                                                    removeItem(index);
                                                }
                                            }} />
                                        </td>
                                    </>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div style={{display: 'flex', alignItems: 'flex-end', width: '700px', marginTop: '5px'}}>
                <div style={{display: 'flex', flexDirection: 'column', flexGrow: 1}}>
                    <div>Observação:</div>
                    <input
                        type="text"
                        style={{width: '100%'}}
                        value={formData.details || ''}
                        name="details"
                        onChange={handleChange}
                    />
                </div>
            </div>

            <div
                className="total-label"
                style={{height: '37.6px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '700px'}}
                data-test="total-value"
            >
                Valor Total: R$ {(formData.totalValue || 0).toLocaleString('pt-BR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
            </div>

            <ActionButtonsManagerView>
                <ConfirmButton onClick={onConfirm} />
            </ActionButtonsManagerView>
        </div>
    );
};

export default ProductSaleManager;
